import { useCallback, useEffect, useMemo, useState } from 'react'
import { Monitor, Building2, AlertTriangle } from 'lucide-react'
import { fetchCustomers } from '@/api/customers'
import { fetchRecentAlerts } from '@/api/dashboard'
import {
  fetchAlertsBySeverity,
  fetchAntivirusSummary,
  fetchMachineTypes,
  fetchM365Summary,
  fetchOnlineStatus,
  fetchOsDistribution,
  fetchPostureSummary,
} from '@/api/charts'
import { CustomerFilter } from '@/components/CustomerFilter'
import {
  DevicesNeedingAttention,
  EstatePostureSummary,
  PostureDrillDown,
} from '@/components/dashboard/EstatePosture'
import {
  Microsoft365DrillDown,
  Microsoft365PostureSummary,
} from '@/components/dashboard/Microsoft365Posture'
import { AntivirusEstatePie } from '@/components/dashboard/AntivirusEstatePie'
import { BackupEstatePie } from '@/components/dashboard/BackupEstatePie'
import { ChartSliceDrillDown } from '@/components/dashboard/ChartSliceDrillDown'
import { RecentAlertsByDevice } from '@/components/dashboard/RecentAlertsByDevice'
import { SimpleBarChart } from '@/components/charts/SimpleBarChart'
import { SimplePieChart } from '@/components/charts/SimplePieChart'
import { shortenOsLabel } from '@/lib/chartLabels'
import { fetchBackupSummary } from '@/api/backup'
import { fetchNasSummary } from '@/api/nas'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/auth/AuthContext'
import { cn } from '@/lib/utils'

const ONLINE_PIE_COLOURS = {
  Online: '#10b981',
  Offline: '#94a3b8',
}

/** High enough for full estate open-alert lists (API max 10000). */
const OPEN_ALERTS_LIMIT = 10000

export function Dashboard() {
  const { isCustomer, canFilterCustomers } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [customers, setCustomers] = useState([])
  const [customerIds, setCustomerIds] = useState([])
  const [online, setOnline] = useState(null)
  const [osData, setOsData] = useState([])
  const [machineTypes, setMachineTypes] = useState([])
  const [alertsBySeverity, setAlertsBySeverity] = useState([])
  const [recentAlerts, setRecentAlerts] = useState([])
  const [posture, setPosture] = useState(null)
  const [postureLoading, setPostureLoading] = useState(true)
  const [postureFilter, setPostureFilter] = useState(null)
  const [m365, setM365] = useState(null)
  const [m365Loading, setM365Loading] = useState(true)
  const [m365Filter, setM365Filter] = useState(null)
  const [backupSummary, setBackupSummary] = useState(null)
  const [nasSummary, setNasSummary] = useState(null)
  const [backupLoading, setBackupLoading] = useState(true)
  const [antivirusSummary, setAntivirusSummary] = useState(null)
  const [antivirusLoading, setAntivirusLoading] = useState(true)
  const [chartFilter, setChartFilter] = useState(null)
  const [alertsOpen, setAlertsOpen] = useState(false)

  useEffect(() => {
    if (canFilterCustomers) {
      fetchCustomers().then(setCustomers).catch(() => {})
    }
  }, [canFilterCustomers])

  const loadDashboard = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [onlineRes, osRes, typesRes, alertsSev, recent] = await Promise.all([
        fetchOnlineStatus(customerIds),
        fetchOsDistribution(customerIds),
        fetchMachineTypes(customerIds),
        fetchAlertsBySeverity(customerIds),
        fetchRecentAlerts(OPEN_ALERTS_LIMIT, customerIds),
      ])
      setOnline(onlineRes)
      setOsData(osRes)
      setMachineTypes(typesRes)
      setAlertsBySeverity(alertsSev)
      setRecentAlerts(recent)
    } catch {
      setError('Could not load dashboard data.')
    } finally {
      setLoading(false)
    }
  }, [customerIds])

  useEffect(() => {
    loadDashboard()
  }, [loadDashboard])

  useEffect(() => {
    setPostureLoading(true)
    setPostureFilter(null)
    setChartFilter(null)
    fetchPostureSummary(customerIds, 10)
      .then(setPosture)
      .catch(() => setPosture(null))
      .finally(() => setPostureLoading(false))
  }, [customerIds])

  useEffect(() => {
    setM365Loading(true)
    setM365Filter(null)
    fetchM365Summary(customerIds)
      .then(setM365)
      .catch(() => setM365(null))
      .finally(() => setM365Loading(false))
  }, [customerIds])

  useEffect(() => {
    setBackupLoading(true)
    setChartFilter(null)
    Promise.all([
      fetchBackupSummary(customerIds).catch(() => null),
      fetchNasSummary(customerIds).catch(() => null),
    ])
      .then(([server, nas]) => {
        setBackupSummary(server)
        setNasSummary(nas)
      })
      .finally(() => setBackupLoading(false))
  }, [customerIds])

  useEffect(() => {
    setAntivirusLoading(true)
    setChartFilter(null)
    fetchAntivirusSummary(customerIds)
      .then(setAntivirusSummary)
      .catch(() => setAntivirusSummary(null))
      .finally(() => setAntivirusLoading(false))
  }, [customerIds])

  function handlePostureFilter(next) {
    setM365Filter(null)
    setChartFilter(null)
    setPostureFilter((current) =>
      current?.area === next.area && current?.status === next.status ? null : next,
    )
  }

  function handleM365Filter(next) {
    setPostureFilter(null)
    setChartFilter(null)
    setM365Filter((current) =>
      current?.area === next.area && current?.status === next.status ? null : next,
    )
  }

  function handleChartFilter(next) {
    setPostureFilter(null)
    setM365Filter(null)
    setChartFilter((current) =>
      current?.chart === next.chart && current?.key === next.key ? null : next,
    )
  }

  const activeAlerts = alertsBySeverity.reduce((sum, row) => sum + (row.value || 0), 0)

  const companyStatLabel = customerIds.length ? 'Companies selected' : 'Customers'

  const companyStatValue = customerIds.length
    ? customerIds.length
    : (customers.length || '—')

  const onlinePie = online
    ? [
        { label: 'Online', value: online.online },
        { label: 'Offline', value: online.offline },
      ].filter((d) => d.value > 0)
    : []

  const alertsCollapsedSummary = useMemo(() => {
    if (loading) return 'Loading…'
    if (!activeAlerts) return 'No open alerts'
    if (recentAlerts.length < activeAlerts) {
      return `${activeAlerts} open · list capped at ${recentAlerts.length}`
    }
    return `${activeAlerts} open alert${activeAlerts === 1 ? '' : 's'}`
  }, [loading, activeAlerts, recentAlerts.length])

  const estateTitle = useMemo(() => {
    if (!canFilterCustomers) {
      return customers[0]?.name || 'Bekat IT Customers'
    }
    if (!customerIds.length) return 'Bekat IT Customers'
    if (customerIds.length === 1) {
      const match = customers.find((c) => Number(c.atera_id) === Number(customerIds[0]))
      return match?.name || 'Selected customer'
    }
    return `${customerIds.length} customers`
  }, [canFilterCustomers, customerIds, customers])

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-destructive">{error}</CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <div className="relative flex flex-col items-center gap-4 text-center">
        {canFilterCustomers ? (
          <div className="w-full sm:absolute sm:left-0 sm:top-1/2 sm:w-auto sm:-translate-y-1/2 sm:text-left">
            <CustomerFilter
              id="dashboard-company"
              label={null}
              value={customerIds}
              onChange={setCustomerIds}
              customers={customers}
              className="w-full sm:w-auto"
            />
          </div>
        ) : null}
        <h2 className="w-full max-w-4xl px-2 text-3xl font-bold tracking-tight text-foreground sm:px-56 sm:text-4xl md:text-5xl">
          <span className="bg-gradient-to-r from-foreground via-foreground to-primary/80 bg-clip-text text-transparent">
            {estateTitle}
          </span>
        </h2>
      </div>

      <div
        className={cn(
          'grid gap-4 sm:grid-cols-2',
          isCustomer ? 'xl:grid-cols-3' : 'xl:grid-cols-4',
        )}
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total devices
            </CardTitle>
            <Monitor className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{online?.total ?? '—'}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Online / offline
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Live Atera status · click a slice to list devices
            </p>
          </CardHeader>
          <CardContent className="min-h-[160px] pt-0">
            {loading ? (
              <Skeleton className="h-[140px] w-full" />
            ) : (
              <SimplePieChart
                data={onlinePie}
                emptyMessage="No devices"
                heightClassName="h-[140px]"
                activeLabel={chartFilter?.chart === 'online' ? chartFilter.key : null}
                onItemClick={(label) =>
                  handleChartFilter({ chart: 'online', key: label })
                }
                colourFor={(entry) =>
                  ONLINE_PIE_COLOURS[entry.label] || ONLINE_PIE_COLOURS.Offline
                }
              />
            )}
          </CardContent>
        </Card>

        {!isCustomer && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {companyStatLabel}
              </CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold">{companyStatValue}</div>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Open alerts
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{activeAlerts || '—'}</div>
            )}
          </CardContent>
        </Card>
      </div>

      <EstatePostureSummary
        data={posture}
        loading={postureLoading}
        selectedFilter={postureFilter}
        onSelectFilter={handlePostureFilter}
      />

      <PostureDrillDown
        filter={postureFilter}
        customerIds={customerIds}
        showCustomer={canFilterCustomers}
        onClose={() => setPostureFilter(null)}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <AntivirusEstatePie
          summary={antivirusSummary}
          loading={antivirusLoading}
          selectedFilter={chartFilter}
          onSelectFilter={handleChartFilter}
        />
        <BackupEstatePie
          serverSummary={backupSummary}
          nasSummary={nasSummary}
          loading={backupLoading}
          selectedFilter={chartFilter}
          onSelectFilter={handleChartFilter}
        />
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">OS distribution</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Top operating systems; remainder grouped as Other. Click a slice to list
              devices.
            </p>
          </CardHeader>
          <CardContent className="min-h-[240px]">
            {loading ? (
              <Skeleton className="h-[180px] w-full" />
            ) : (
              <SimplePieChart
                data={osData}
                formatLabel={shortenOsLabel}
                heightClassName="h-[180px]"
                activeLabel={chartFilter?.chart === 'os' ? chartFilter.key : null}
                onItemClick={(label) => handleChartFilter({ chart: 'os', key: label })}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <ChartSliceDrillDown
        filter={chartFilter}
        customerIds={customerIds}
        showCustomer={canFilterCustomers}
        onClose={() => setChartFilter(null)}
      />

      <Microsoft365PostureSummary
        data={m365}
        loading={m365Loading}
        selectedFilter={m365Filter}
        onSelectFilter={handleM365Filter}
      />

      <Microsoft365DrillDown
        filter={m365Filter}
        customerIds={customerIds}
        showCustomer={canFilterCustomers}
        onClose={() => setM365Filter(null)}
      />

      <DevicesNeedingAttention
        customerIds={customerIds}
        total={posture?.attention_total}
        reasonCounts={posture?.attention_reason_counts}
        loading={postureLoading}
        showCustomer={canFilterCustomers}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Machine types</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[180px] w-full" />
            ) : (
              <SimpleBarChart data={machineTypes} />
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Open alerts by severity</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[180px] w-full" />
            ) : (
              <SimpleBarChart
                data={alertsBySeverity}
                formatLabel={(l) => l || 'Unknown'}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <CollapsePanel
        id="recent-open-alerts"
        title="Recent open alerts"
        collapsedSummary={alertsCollapsedSummary}
        open={alertsOpen}
        onOpenChange={setAlertsOpen}
        pill={loading ? undefined : String(activeAlerts)}
        pillVariant={activeAlerts > 0 ? 'pending' : 'inactive'}
        bodyClassName="space-y-3"
      >
        <p className="text-xs text-muted-foreground">
          {activeAlerts > recentAlerts.length
            ? `Showing ${recentAlerts.length} of ${activeAlerts} open alerts (list cap reached), grouped by machine. `
            : `${recentAlerts.length} open alert${recentAlerts.length === 1 ? '' : 's'}, grouped by machine. `}
          Click a hostname to open the device.
        </p>
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : recentAlerts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No open alerts.</p>
        ) : (
          <RecentAlertsByDevice alerts={recentAlerts} />
        )}
      </CollapsePanel>
    </div>
  )
}
